import type { BannerProps, BannerSubComponents } from './Banner.types'
import cn from 'classnames'
import * as React from 'react'
import { Icon } from '../icon'
import { ActionsWrapper } from './actions-wrapper'
import styles from './Banner.module.css'
import { iconMapping } from './Banner.types'
import { BannerContext } from './BannerContext'
import { ContentWrapper } from './content-wrapper'
import { SupportingText, Text, TextsWrapper } from './text-wrapper'

const colorClassMap = {
  neutral: styles.colorNeutral,
  informative: styles.colorInformative,
  danger: styles.colorDanger,
  warning: styles.colorWarning,
  success: styles.colorSuccess,
}

const Banner: React.FunctionComponent<BannerProps> & BannerSubComponents = ({
  color,
  hasIcon,
  display = 'inline',
  text,
  supportingText,
  supportingTextHtml,
  size,
  className,
  icon,
  contentIsCentered = false,
  actionsPosition,
  children,
}) => {
  const getIconGlyph = () => {
    if ((color === 'neutral' || color === 'informative') && icon) {
      return icon
    }
    return iconMapping[color]
  }

  const iconGlyph = getIconGlyph()
  const isAlert = color === 'danger' || color === 'warning'

  const textOnly = !!text && !children && !supportingText && !supportingTextHtml
  return (
    <BannerContext value={{ size }}>
      <div
        role={isAlert ? 'alert' : 'status'}
        aria-live={isAlert ? undefined : 'polite'}
        className={cn(
          styles.banner,
          colorClassMap[color],
          { [styles.overlay]: display === 'overlay' },
          className,
        )}
      >
        <div className={cn(styles.content, { [styles.contentCentered]: contentIsCentered || textOnly })}>
          {hasIcon && (
            <Icon
              size={size === 'S' ? 24 : 32}
              glyph={iconGlyph}
              aria-hidden="true"
              className={styles.icon}
            />
          )}
          {!children && (
            <ContentWrapper actionsPosition={actionsPosition}>
              <TextsWrapper>
                <Text size={size}>{text}</Text>
                {supportingText && <SupportingText size={size} innerHTML={supportingText} />}
                {supportingTextHtml}
              </TextsWrapper>
            </ContentWrapper>
          )}
          {children}
        </div>
      </div>
    </BannerContext>
  )
}

Banner.displayName = 'Banner'
Banner.ActionsWrapper = ActionsWrapper
Banner.TextsWrapper = TextsWrapper
Banner.ContentWrapper = ContentWrapper
Banner.Text = Text
Banner.SupportingText = SupportingText

export { Banner }
